import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDispatch, useSelector } from "react-redux";
import * as para from "../../../redux/parameter/actions";
import SnackBar from "../../../components/Snackbars/CustomizedSnackbars";
import Buttons from "../../../components/Controls/Buttons/Button";
import Label from "../../../components/Label/Label";
import Loader from "../../../components/Loader/loader";

const NON_DRAGGABLE_COLUMNS = ["Batch ID", "Batch Date", "Comments"];

/* ================= HELPER ================= */
const getSafeColumnId = (col, index) => {
  return col.column_id ?? `temp-${col.column_name}-${index}`;
};

/* ================= DRAGGABLE HEADER ================= */
const DraggableHeader = ({ column }) => {
  const isDraggable = !NON_DRAGGABLE_COLUMNS.includes(column.column_name);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: column.column_id,
      disabled: !isDraggable,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDraggable ? "grab" : "not-allowed",
    opacity: isDraggable ? 1 : 0.6,
    fontWeight: 600,
    background: "#E9E9E9",
    border: "1px solid #D5D7DA",
  };

  return (
    <TableCell ref={setNodeRef} style={style}>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ minHeight: '20px', minWidth: 'max-content' }}>
        {column.column_name}
        {isDraggable && (
          <DragIndicatorIcon
            fontSize="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab", color: "#666" }}
          />
        )}
      </Box>
    </TableCell>
  );
};

/* ================= DRAGGABLE TAB ================= */
const DraggableTab = ({ sheet, index, isActive, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: sheet.sheet_order,
      disabled: index === 0,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginRight: 1,
  };

  return (
    <Button
      ref={setNodeRef}
      variant={isActive ? "contained" : "outlined"}
      onClick={() => onClick(sheet)}
      sx={{ ...style, display: "flex", gap: 1, maxHeight: "40px", minWidth: 'max-content' }}
    >
      {sheet.sheet_name}
      {index !== 0 && (
        <DragIndicatorIcon
          fontSize="small"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </Button>
  );
};

/* ================= MAIN COMPONENT ================= */
const ExcelView = ({ selectedUnitOpid }) => {
  const dispatch = useDispatch();
  const process = useSelector((state) => state?.process);

  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [columns, setColumns] = useState([]);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [isColumnDirty, setIsColumnDirty] = useState(false);
  const [isSheetDirty, setIsSheetDirty] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  /* ================= FETCH ================= */
  const getPreviewTemplate = async () => {
    const res = await dispatch(
      para.templateView({ unit_op_id: selectedUnitOpid })
    );

    const fetchedSheets = res?.data?.sheets || [];

    setSheets(fetchedSheets);

    if (fetchedSheets.length > 0) {
      const firstSheet = fetchedSheets[0];

      const normalizedColumns = firstSheet.columns.map((col, index) => ({
        ...col,
        column_id: getSafeColumnId(col, index),
      }));

      setActiveSheet(firstSheet);
      setColumns(normalizedColumns);
    }
  };

  useEffect(() => {
    getPreviewTemplate();
  }, []);

  /* ================= DRAG START ================= */
  const handleColumnDragStart = (event) => {
    const { active } = event;

    const draggedColumn = columns.find((c) => c.column_id === active.id);

    if (
      draggedColumn &&
      NON_DRAGGABLE_COLUMNS.includes(draggedColumn.column_name)
    ) {
      setSnack({
        open: true,
        message: `${draggedColumn.column_name} cannot be moved`,
        severity: "warning",
      });
    }
  };

  /* ================= DRAG END ================= */
  const handleColumnDragEnd = (event) => {
    const { active, over } = event;

    if (!active?.id || !over?.id) return;
    if (active.id === over.id) return;

    const oldIndex = columns.findIndex(c => c.column_id === active.id);
    const newIndex = columns.findIndex(c => c.column_id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const draggedColumn = columns[oldIndex];

    // 🚫 1. Prevent dragging fixed column itself
    if (NON_DRAGGABLE_COLUMNS.includes(draggedColumn.column_name)) {
      setSnack({
        open: true,
        message: `${draggedColumn.column_name} cannot be moved`,
        severity: "warning",
      });
      return;
    }

    // 🔥 2. Get all fixed column indexes
    const fixedIndexes = columns
      .map((col, index) =>
        NON_DRAGGABLE_COLUMNS.includes(col.column_name) ? index : -1
      )
      .filter(index => index !== -1);

    // 🚫 3. Prevent crossing any fixed column
    const isCrossingFixed = fixedIndexes.some((fixedIndex) => {
      return (
        (oldIndex < fixedIndex && newIndex >= fixedIndex) || // moving right across fixed
        (oldIndex > fixedIndex && newIndex <= fixedIndex)    // moving left across fixed
      );
    });

    if (isCrossingFixed) {
      setSnack({
        open: true,
        message: `Cannot move columns across fixed columns`,
        severity: "warning",
      });
      return;
    }

    // ✅ 4. Allow reorder
    const updatedColumns = arrayMove(columns, oldIndex, newIndex);

    const updatedSheets = sheets.map(sheet => {
      if (sheet.sheet_order === activeSheet.sheet_order) {
        return {
          ...sheet,
          columns: updatedColumns
        };
      }
      return sheet;
    });

    setSheets(updatedSheets);
    setColumns(updatedColumns);
    setIsColumnDirty(true);
  };

  /* ================= SHEET DRAG ================= */
  const handleSheetDragEnd = (event) => {
    const { active, over } = event;

    if (!active?.id || !over?.id) return;
    if (active.id === over.id) return;

    const oldIndex = sheets.findIndex((s) => s.sheet_order === active.id);
    const newIndex = sheets.findIndex((s) => s.sheet_order === over.id);

    if (newIndex === 0) return;

    const updatedSheets = arrayMove(sheets, oldIndex, newIndex);
    setSheets(updatedSheets);
    setIsSheetDirty(true);
  };

  /* ================= UPDATE ================= */
  const handleUpdate = async () => {
    const columnPayload = {
      unit_op_id: selectedUnitOpid,
      sheet_order: activeSheet.sheet_order,
      parameters: columns
        .filter((c) => !c.column_id.toString().startsWith("temp-"))
        .map((c) => c.column_id),
    };

    const sheetPayload = {
      unit_op_id: selectedUnitOpid,
      sheets: sheets.map((s) => s.sheet_order),
    };

    if (isColumnDirty) {
      const res = await dispatch(para.reOrderColumn(columnPayload));
      setSnack({
        open: true,
        message: res.data.message || "Column order updated",
        severity: "success",
      });
      setIsColumnDirty(false);
      dispatch(para.getParaList({
        page: 1,
        page_size: 50,
      }))
    }

    if (isSheetDirty) {
      const res = await dispatch(para.reOrderSheet(sheetPayload));
      setSnack({
        open: true,
        message: res.data.message || "Sheet order updated",
        severity: "success",
      });
      setIsSheetDirty(false);
    }

    getPreviewTemplate();
  };

  const handleActiveSheetChange = (sheet) => {
    setActiveSheet(sheet);

    const normalizedColumns = sheet.columns.map((col, index) => ({
      ...col,
      column_id: getSafeColumnId(col, index),
    }));

    setColumns(normalizedColumns);
  };

  /* ================= UI ================= */
  return (
    <Box height="calc(100vh - 155px)" display="flex" flexDirection="column">
      <TableContainer component={Paper} sx={{ flex: 1 }}>
        {activeSheet && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleColumnDragStart}
            onDragEnd={handleColumnDragEnd}
          >
            <Table stickyHeader>
              <TableHead>
                <SortableContext
                  items={columns.map((c) => c.column_id)}
                  strategy={horizontalListSortingStrategy}
                >
                  <TableRow>
                    {columns.map((col) => (
                      <DraggableHeader key={col.column_id} column={col} />
                    ))}
                  </TableRow>
                </SortableContext>
              </TableHead>

              <TableBody>
                {activeSheet?.rows?.map((row) => (
                  <TableRow key={row.id}>
                    {columns.map((col) => (
                      <TableCell key={col.column_id}>
                        {row[col.column_id] || ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DndContext>
        )}
      </TableContainer>

      {process?.templateView?.loading && <Loader />}

      {snack.open && (
        <SnackBar
          handleClose={() =>
            setSnack({ open: false, message: "", severity: "" })
          }
          severity={snack.severity}
          state={snack.open}
          text={snack.message}
        />
      )}

      {/* SHEETS */}
      <Box display="flex" justifyContent="space-between" gap={1}>
        <Box display="flex" sx={{
          overflowX: "auto", pt: 1, maxHeight: "50px", scrollbarWidth: "thin",
        }}>
          <DndContext onDragEnd={handleSheetDragEnd}>
            <SortableContext
              items={sheets.map((s) => s.sheet_order)}
              strategy={horizontalListSortingStrategy}
            >
              {sheets.map((sheet, index) => (
                <DraggableTab
                  key={sheet.sheet_order}
                  sheet={sheet}
                  index={index}
                  isActive={activeSheet?.sheet_order === sheet.sheet_order}
                  onClick={handleActiveSheetChange}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>

        <Buttons
          disabled={!isColumnDirty && !isSheetDirty}
          onClick={handleUpdate}
          text="Update"
          sx={{ mt: 1, height: "32px" }}
        />
      </Box>
    </Box>
  );
};

export default ExcelView;